import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Car, Tent, GraduationCap, Wheat, Thermometer, CloudLightning, Gauge } from "lucide-react";

export const IMPACT_ICONS = { travel: Car, outdoor: Tent, schools: GraduationCap, agriculture: Wheat, heat: Thermometer, severe: CloudLightning };
export const LEVEL_STYLE = {
  low: { label: "Low", color: "#22c55e", bg: "rgba(34,197,94,0.14)" },
  moderate: { label: "Moderate", color: "#ffd60a", bg: "rgba(244,162,97,0.16)" },
  high: { label: "High", color: "#ef233c", bg: "rgba(239,35,60,0.18)" },
};

export function useImpact() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/impact").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 600_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

export function LevelPill({ level, size = "sm" }) {
  const st = LEVEL_STYLE[level] || LEVEL_STYLE.low;
  const dots = level === "high" ? 3 : level === "moderate" ? 2 : 1;
  return (
    <span
      data-testid={`impact-level-${level}`}
      className={`inline-flex items-center gap-1.5 rounded-full font-oswald font-bold uppercase tracking-widest ${size === "lg" ? "px-3 py-1 text-xs" : "px-2.5 py-0.5 text-[10px]"}`}
      style={{ color: st.color, background: st.bg, border: `1px solid ${st.color}55` }}
    >
      <span className="flex items-center gap-0.5">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ background: i < dots ? st.color : "rgba(255,255,255,0.15)" }} />
        ))}
      </span>
      {st.label} impact
    </span>
  );
}

export function ImpactCard({ cat, compact = false }) {
  const Icon = IMPACT_ICONS[cat.id] || Gauge;
  const st = LEVEL_STYLE[cat.level] || LEVEL_STYLE.low;
  return (
    <div
      data-testid={`impact-card-${cat.id}`}
      className="glass lift relative flex flex-col overflow-hidden rounded-sm"
      style={{ borderLeft: `4px solid ${st.color}` }}
    >
      <div className={`flex items-start justify-between gap-3 ${compact ? "p-4" : "p-5"}`}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-sm" style={{ background: st.bg }}>
            <Icon className="h-5 w-5" style={{ color: st.color }} />
          </div>
          <div className={`font-oswald font-bold uppercase tracking-wider text-white ${compact ? "text-2xl" : "text-xl"}`}>{cat.label}</div>
        </div>
        <LevelPill level={cat.level} />
      </div>
      <div className={`${compact ? "px-4 pb-4" : "px-5 pb-5"}`}>
        <p className={`text-gray-100 ${compact ? "text-lg leading-snug lg:text-xl" : "text-base leading-snug"}`}>{cat.summary}</p>
        {cat.drivers?.length > 0 && (
          <ul className="mt-3 space-y-1">
            {cat.drivers.map((d) => (
              <li key={d} className={`flex items-start gap-2 text-gray-300 ${compact ? "text-sm lg:text-base" : "text-xs"}`}>
                <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full" style={{ background: st.color }} />
                {d}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function WeatherImpact() {
  const data = useImpact();
  if (!data) return null;
  return (
    <section id="impact" data-testid="weather-impact-section" className="relative py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">What it means for you</div>
            <h2 className="font-oswald text-2xl font-bold uppercase tracking-wider text-white sm:text-3xl lg:text-4xl">Georgia Weather Impact</h2>
            <p className="mt-2 max-w-2xl text-sm text-gray-300 md:text-base" data-testid="impact-headline">{data.headline}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs uppercase tracking-widest text-gray-400">{data.window}</span>
            <LevelPill level={data.overall} size="lg" />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.categories.map((c) => <ImpactCard key={c.id} cat={c} />)}
        </div>
        <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-gray-500">
          Impact levels are derived from NWS forecasts, active alerts and SPC outlooks · updated every 10 min
        </div>
      </div>
    </section>
  );
}
